import {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import WellnessDataEngine from "@/utils/WellnessDataEngine";

const WellnessContext = createContext(null);

export function WellnessProvider({ children }) {
  const engineRef = useRef(new WellnessDataEngine());
  const [loadedPeriods, setLoadedPeriods] = useState({});
  const [loadingPeriods, setLoadingPeriods] = useState({});
  const [periodErrors, setPeriodErrors] = useState({});

  const engine = engineRef.current;

  useEffect(() => {
    if (!engine) {
      return;
    }

    const onLoadingStart = ({ key }) => {
      setLoadingPeriods((p) => ({ ...p, [key]: true }));
      setPeriodErrors((e) => {
        const copy = { ...e };
        delete copy[key];

        return copy;
      });
    };

    const onLoadingEnd = ({ key }) => {
      setLoadingPeriods((p) => {
        const copy = { ...p };
        delete copy[key];
        return copy;
      });
    };

    const onPeriodLoaded = ({ key }) => {
      setLoadedPeriods((p) => ({ ...p, [key]: true }));
      setLoadingPeriods((l) => {
        const copy = { ...l };
        delete copy[key];
        return copy;
      });

      setPeriodErrors((e) => {
        const copy = { ...e };
        delete copy[key];
        return copy;
      });
    };

    const onLoadingError = ({ key, error }) => {
      setLoadingPeriods((p) => {
        const copy = { ...p };
        delete copy[key];
        return copy;
      });

      setPeriodErrors((e) => ({ ...e, [key]: e }));
    };

    const onReset = () => {
      setLoadedPeriods({});
      setLoadingPeriods({});
      setPeriodErrors({});
    };

    const offLoadingStart = engine.on("loading-start", onLoadingStart);
    const offLoadingEnd = engine.on("loading-end", onLoadingEnd);
    const offPeriodLoaded = engine.on("period-loaded", onPeriodLoaded);
    const offLoadingError = engine.on("loading-error", onLoadingError);
    const offReset = engine.on("reset", onReset);

    return () => {
      offLoadingStart();
      offLoadingEnd();
      offPeriodLoaded();
      offLoadingError();
      offReset();
    };
  }, [engine]);

  const loadPeriod = useCallback(
    async (key, loaderFn) => {
      return engine.guardLoad(key, loaderFn);
    },
    [engine]
  );

  const value = useMemo(
    () => ({
      engine,
      loadedPeriods,
      loadingPeriods,
      periodErrors,
      loadPeriod,
      reset: () => engine.reset(),
      getAllRaw: () => engine.getAllRaw(),
      getByUserId: (id) => engine.getByUserId(id),
      stats: () => engine.stats(),
      findInRaw: (fn) => engine.findInRaw(fn),
    }),
    [loadedPeriods, engine, loadingPeriods, periodErrors, loadPeriod]
  );

  return (
    <WellnessContext.Provider value={value}>
      {children}
    </WellnessContext.Provider>
  );
}

export function useWellness() {
  const ctx = useContext(WellnessContext);
  if (!ctx) {
    throw new Error("useWellness must be used within WellnessProvider");
  }

  return ctx;
}
